import {
  Button,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@growdash/design-system/atoms';
import { ComboBox, MultiSelect } from '@growdash/design-system/molecules';
import { IPageFilters } from '@growdash/growintel/constants/Tabs';
import { useMetaData, useTabState } from '@growdash/growintel/hooks';
import { useEffect, useState } from 'react';
import SelectBrand from '../../../components/molecules/select-brand';

type Props = {
  isLoading?: boolean;
  isEmptyState?: boolean;
};

export default function TrendsFiltersView({ isLoading = false }: Props) {
  const {
    cities,
    cuisines,
    areas: areasMetaData,
    areasWithCity,
  } = useMetaData();

  const { filters, updateTabState } = useTabState('trends');
  const { cuisine, brand, areas, city } = filters;

  const [tempCuisine, setTempCuisine] = useState(cuisine);
  const [tempBrand, setTempBrand] = useState(brand);

  const [tempCity, setTempCity] = useState(city);
  const [tempAreas, setTempAreas] = useState(areas);

  const [areaOptions, setAreaOptions] = useState<string[]>([]);
  useEffect(() => {
    setAreaOptions(
      areasWithCity.filter((area) => area.city === tempCity).map((a) => a.area),
    );
    setTempAreas([]);
  }, [tempCity]);

  const handleCuisineChange = (value: string) => {
    setTempCuisine(value);
    setTempBrand(null);
    setTempCity('');
    setTempAreas([]);
    handleUpdatefilter({
      brand: null,
      city: '',
      areas: [],
      cuisine: value,
    });
  };

  const handleUpdatefilter = (payload: IPageFilters['trends']) => {
    updateTabState({
      filters: payload,
    });
  };

  const handleClearFilters = () => {
    setTempCity('');
    setTempBrand(null);
    setTempCuisine('');
    setTempAreas([]);
  };

  const isSubmitDisabled = isLoading || !tempCuisine || !tempBrand;

  return (
    <div className="flex gap-4 items-center flex-wrap">
      <div className="flex items-center gap-4 max-lg:flex-wrap">
        <div className="flex flex-col gap-2">
          <Label>Cuisine*</Label>
          <ComboBox
            className="md:min-w-44"
            options={cuisines.map((c) => ({
              label: c,
              value: c,
            }))}
            disabled={!cuisines?.length}
            placeholder={
              !cuisines?.length ? 'Cuisines Loading...' : 'Select a Cuisine'
            }
            inputPlaceholder="Search cuisines..."
            value={tempCuisine ?? ''}
            onValueChange={handleCuisineChange}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>Insights for*</Label>
          <SelectBrand
            className="md:min-w-44"
            disabled={!tempCuisine}
            cuisine={tempCuisine}
            onValueChange={setTempBrand}
            value={tempBrand?.id ?? ''}
          />
        </div>
        {tempCuisine && tempBrand?.id && (
          <div className={'flex flex-col gap-2 animate-slide-in-right'}>
            <Label>City</Label>

            <ComboBox
              disabled={!tempCuisine || !tempBrand}
              className="md:min-w-44"
              options={[
                {
                  label: 'All Cities',
                  value: 'ALL',
                },
                ...cities.map((c) => ({
                  label: c,
                  value: c,
                })),
              ]}
              placeholder={
                !cities?.length ? 'Cities Loading...' : 'Select a City'
              }
              inputPlaceholder="Search city..."
              value={tempCity?.length ? tempCity : 'ALL'}
              onValueChange={setTempCity}
            />
          </div>
        )}

        {areasMetaData?.length > 0 && tempCuisine && tempBrand && tempCity && (
          <div className="flex flex-col gap-2 animate-slide-in-right">
            <Label>Area</Label>
            <MultiSelect
              key={tempCity}
              showSelectAll
              value={tempAreas?.length ? tempAreas : areaOptions}
              className="md:min-w-44"
              placeholder="Areas"
              options={areaOptions.map((a) => ({
                label: a,
                value: a,
              }))}
              onValueChange={(value) => {
                setTempAreas(value);
              }}
            />
          </div>
        )}
        <Tooltip>
          <TooltipTrigger className="mt-auto">
            <div className="flex items-center">
              <Button
                onClick={() => {
                  handleUpdatefilter({
                    areas: tempAreas,
                    brand: tempBrand,
                    city: tempCity,
                    cuisine: tempCuisine,
                  });
                }}
                disabled={isSubmitDisabled}
              >
                Show Insights
              </Button>
              {!isSubmitDisabled && (
                <Button
                  onClick={handleClearFilters}
                  variant="link"
                  className="animate-fade-in text-secondary underline-offset-[5px] underline"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent hidden={!isSubmitDisabled}>
            Select a cuisine and a brand to get started
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
